import { z } from 'zod';

import { isBlockedHttpHost } from './automation-graph.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MÜŞTERİNİN KENDİ MCP SUNUCULARI — sözleşme (1 Eki 2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ürün sahibi: *"SDK'da, Claude'da olan tüm özellikleri ekleyelim."* Bu parça
 * Anthropic Messages API'nin MCP BAĞLAYICISI (beta `mcp-client-2025-11-20`):
 * istekte `mcp_servers` + araç türü `mcp_toolset`. Müşteri kendi uzak MCP
 * sunucusunu (HubSpot, Notion, kendi CRM'i…) bağlıyor; sohbet AI'ı ve arka
 * plan operatörü onun araçlarını kullanıyor. Senaryo denetimindeki "CRM
 * bağlayıcısı yok" açığını kapatıyor.
 *
 * ---------------------------------------------------------------------------
 * 🔴 Neden bir GÜVENLİK AYARI (yalnız panel, ADMIN+)
 * ---------------------------------------------------------------------------
 * MCP aracını BİZ çağırmıyoruz — Anthropic, isteğin içinde, kendi altyapısından
 * çağırıyor ve sonucu aynı yanıtın içinde döndürüyor. Yani bizim onay kartımız
 * (`decideOp`, öneri akışı) bir MCP aracının KENDİ yan etkisinin (CRM'de kayıt
 * silme, e-posta gönderme) ÖNÜNE GEÇEMEZ. Tek denetim noktası hangi araçların
 * isteğe girdiği: izin listesi. Bu yüzden:
 *   · ekleme/düzenleme/silme yalnız panelden, `connection.manage` (ADMIN+);
 *     API anahtarıyla değil, AI araçlarıyla hiç değil (ürün sahibi: "güvenlik
 *     ayarları insanda") — sohbet yalnız `panel_link` ile sayfayı verir
 *   · yazabilen araçlar varsayılan KAPALI; açarken ekranda uyarı
 *   · MCP sonucu okunan turda para/erişim/veri paylaşımı tam otonomide bile
 *     karta düşer (web lekesiyle AYNI kural — `platform-ops/policy.ts`)
 */

// ═══════════════════════════════════════════════════════════════════════════
// Sabitler
// ═══════════════════════════════════════════════════════════════════════════

/** Yetkilendirme türü. Jeton (bearer ya da OAuth erişim jetonu) `TokenVault`ta şifreli. */
export const MCP_CONNECTOR_AUTH_TYPES = ['NONE', 'BEARER', 'OAUTH'] as const;
export type McpConnectorAuthType = (typeof MCP_CONNECTOR_AUTH_TYPES)[number];

/**
 * Araç izin modu.
 *   · `ALLOWLIST` — yalnız `allowedTools` (varsayılan; ekleme anında okuma araçlarıyla dolar)
 *   · `ALL`       — sunucunun BÜTÜN araçları, sonradan eklenenler dahil (ekranda yazma uyarısı)
 *
 * Neden "boş liste = hepsi" DEĞİL: yalnız yazma aracı olan bir sunucuda
 * güvenli varsayılan BOŞ liste olurdu ve o "hepsi" diye okunurdu — tam da
 * kapatmak istediğimiz şey. Mod açık bir seçim.
 */
export const MCP_CONNECTOR_TOOL_MODES = ['ALLOWLIST', 'ALL'] as const;
export type McpConnectorToolMode = (typeof MCP_CONNECTOR_TOOL_MODES)[number];

/** Çalışma alanı başına bağlantı tavanı — her sunucu her isteğe araç tanımı ekliyor. */
export const MCP_CONNECTORS_MAX_PER_WORKSPACE = 10;

/** İzin listesinin uzunluk tavanı. */
export const MCP_CONNECTOR_MAX_ALLOWED_TOOLS = 200;

/** Ekranda/kayıtta saklanan araç kataloğunun tavanı (tools/list). */
export const MCP_CONNECTOR_MAX_CATALOG_TOOLS = 300;

/**
 * İstek anında Anthropic sunucuya ulaşamadıysa bağlantı bu kadar süre
 * isteklere EKLENMEZ — her turun aynı 400'ü yiyip yeniden denemesi yerine.
 * Ayar ekranında "Test et" bekleme süresini sıfırlıyor.
 */
export const MCP_CONNECTOR_FAILURE_PAUSE_MINUTES = 15;

/** OAuth `state` ömrü (saniye) — reklam platformu akışıyla aynı pencere. */
export const MCP_OAUTH_STATE_TTL_SECONDS = 10 * 60;

/** OAuth geri dönüş yolu (API'de, `@Public`). Tam adres `API_PUBLIC_URL` + bu. */
export const MCP_OAUTH_CALLBACK_PATH = '/mcp-connectors/oauth/callback';

/** Panelde ayar sayfası (çalışma alanı öneki olmadan — `panel_link` ve yönlendirme). */
export const MCP_CONNECTORS_PANEL_PATH = '/dashboard/settings/mcp';

/** Anthropic'in istek başlığı (beta). */
export const MCP_CLIENT_BETA = 'mcp-client-2025-11-20';

/**
 * Hata kodları — ekran çeviriyor (`settings.mcp.error.<KOD>`), sunucu metin
 * göndermiyor. Sağlayıcı/sunucu ham metni kullanıcıya GİTMİYOR.
 */
export const MCP_CONNECTOR_ERROR_CODES = [
  /// Adres https değil, ayrıştırılamıyor ya da kullanıcı adı/parola taşıyor.
  'URL_INVALID',
  /// Özel/yerel ağ adresi (ad bile olsa çözüldüğü adres) ya da Mentorivo'nun kendisi.
  'URL_BLOCKED',
  'DNS_FAILED',
  'CONNECTION_FAILED',
  'TIMEOUT',
  /// Sunucu yönlendirdi — takip edilmiyor (SSRF); doğru adresi girin.
  'REDIRECTED',
  /// 401/403 — jeton yok, geçersiz ya da süresi dolmuş.
  'UNAUTHORIZED',
  /// Sunucu bir HTTP hatası döndü (4xx/5xx, yetki dışı).
  'HTTP_ERROR',
  /// Yanıt bir MCP (JSON-RPC) yanıtı değil.
  'NOT_MCP',
  /// Sunucu yalnız eski SSE aktarımını konuşuyor — araç listesi testten okunamadı
  /// (AI yine bağlanabilir; Anthropic iki aktarımı da destekliyor).
  'SSE_ONLY',
  /// Yanıt tavanı aşıldı.
  'TOO_LARGE',
  /// OAuth: sunucu yetkilendirme sunucusunu bildirmiyor / meta veri okunamadı.
  'OAUTH_DISCOVERY_FAILED',
  /// OAuth: dinamik istemci kaydı desteklenmiyor ya da reddedildi.
  'OAUTH_REGISTRATION_FAILED',
  /// OAuth: kod → jeton takası başarısız.
  'OAUTH_EXCHANGE_FAILED',
  /// OAuth: jeton yenilenemedi — yeniden yetkilendirin.
  'OAUTH_REFRESH_FAILED',
  /// OAuth: kullanıcı izni reddetti.
  'OAUTH_DENIED',
  /// OAuth: akış süresi doldu ya da `state` tanınmadı.
  'OAUTH_STATE_INVALID',
  /// İstek anında yapay zekâ servisi sunucuya bağlanamadı.
  'AI_UNREACHABLE',
  /// İstek anında yapay zekâ servisi sunucudan yetki hatası aldı.
  'AI_UNAUTHORIZED',
  /// Kayıtlı jeton çözülemedi (anahtar değişti) — jetonu yeniden girin.
  'TOKEN_UNREADABLE',
] as const;
export type McpConnectorErrorCode = (typeof MCP_CONNECTOR_ERROR_CODES)[number];

// ═══════════════════════════════════════════════════════════════════════════
// Araç sınıflandırması — "yazabilir mi"
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `read`    — sunucu `annotations.readOnlyHint: true` diyor
 * `write`   — sunucu açıkça yazdığını söylüyor (`readOnlyHint: false` ya da
 *             `destructiveHint: true`)
 * `unknown` — ipucu yok. YAZABİLİR sayılıyor (MCP şartnamesinde
 *             `readOnlyHint` varsayılanı `false`).
 *
 * ⚠️ İpuçları SUNUCUNUN beyanı, güvence değil — kötü niyetli bir sunucu yazan
 * aracını "salt okuma" diye işaretleyebilir. Ekran bunu söylüyor; asıl kemer
 * müşterinin kendi seçtiği sunucuya güvenmesi + izin listesi.
 */
export type McpToolKind = 'read' | 'write' | 'unknown';

export interface McpToolInfo {
  /** Sunucunun verdiği ad (izin listesinin anahtarı). */
  name: string;
  /** `annotations.title` ya da `title` — yoksa `null`. */
  title: string | null;
  /** Kısaltılmış açıklama (ekran için; modele sunucunun kendisi veriyor). */
  description: string | null;
  kind: McpToolKind;
  /** `destructiveHint: true` — silme/üzerine yazma. */
  destructive: boolean;
  /**
   * Önerilen varsayılan — ekleme anında izin listesine giriyor. Yalnız
   * okuma araçları: `read`, ya da ipucu yokken adı açıkça okuma fiiliyle
   * başlayan (`get_`, `list_`, `search_`…) ve `destructive` olmayan araç.
   */
  suggested: boolean;
}

/** Ekranda ve kayıtta tutulan açıklama uzunluğu. */
export const MCP_TOOL_DESCRIPTION_MAX = 400;

/** Araç adı — MCP şartnamesi 1-128 karakter, `[A-Za-z0-9_.-]` öneriyor; ekran için biraz geniş. */
export const mcpToolNameSchema = z
  .string()
  .min(1)
  .max(128)
  .regex(/^[A-Za-z0-9_.\-/:]+$/, 'MCP_TOOL_NAME_INVALID');

const READ_VERB = /^(get|list|search|find|read|fetch|query|lookup|describe|show|view|count|retrieve|check|browse|export_?read)([_\-.]|[A-Z]|$)/i;

/**
 * `tools/list` öğesinden sınıf. Saf; sunucunun bozuk/eksik alanlarına dayanıklı.
 */
export function classifyMcpTool(tool: {
  name: string;
  annotations?: { readOnlyHint?: unknown; destructiveHint?: unknown } | null;
}): { kind: McpToolKind; destructive: boolean; suggested: boolean } {
  const hints = tool.annotations ?? null;
  const destructive = hints?.destructiveHint === true;
  let kind: McpToolKind = 'unknown';
  if (hints?.readOnlyHint === true && !destructive) kind = 'read';
  else if (hints?.readOnlyHint === false || destructive) kind = 'write';
  const suggested = kind === 'read' || (kind === 'unknown' && READ_VERB.test(tool.name));
  return { kind, destructive, suggested };
}

// ═══════════════════════════════════════════════════════════════════════════
// Adres doğrulaması
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Bağlantı adresi kapısı (şema düzeyi). Asıl SSRF kemeri sunucuda: adın
 * ÇÖZÜLDÜĞÜ her adres de süzülüyor ve doğrulanan adrese bağlanılıyor
 * (`http-action.service.ts` deseni).
 *
 *   · yalnız `https` (Anthropic'in de şartı)
 *   · kullanıcı adı/parola taşıyan adres yok (`https://user:pass@…` — sır
 *     adreste kalır, kayda/loga düşer)
 *   · özel/yerel ağ ana adı ya da değişmezi yok
 *   · Mentorivo'nun kendi alan adları yok — kendi MCP sunucumuzu ajana
 *     bağlamak döngü; ajanın o araçları zaten var
 */
export function mcpConnectorUrlProblem(raw: string): McpConnectorErrorCode | null {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return 'URL_INVALID';
  }
  if (url.protocol !== 'https:') return 'URL_INVALID';
  if (url.username !== '' || url.password !== '') return 'URL_INVALID';
  const host = url.hostname.replace(/^\[|\]$/g, '').toLowerCase().replace(/\.+$/, '');
  if (host.length === 0) return 'URL_INVALID';
  if (isBlockedHttpHost(host)) return 'URL_BLOCKED';
  if (host === 'mentorivo.com' || host.endsWith('.mentorivo.com')) return 'URL_BLOCKED';
  return null;
}

export const mcpConnectorUrlSchema = z
  .string()
  .trim()
  .min(8)
  .max(2000)
  .superRefine((value, ctx) => {
    const problem = mcpConnectorUrlProblem(value);
    if (problem) ctx.addIssue({ code: 'custom', message: `MCP_${problem}` });
  });

/** Bearer jetonu — yalnız gövdede gelir, hiçbir yanıtta geri dönmez. */
const bearerTokenSchema = z
  .string()
  .trim()
  .min(8)
  .max(8192)
  .regex(/^[\x21-\x7e]+$/, 'MCP_TOKEN_INVALID');

const connectorNameSchema = z.string().trim().min(1).max(60);

/**
 * Önceden kayıtlı OAuth istemcisi — sunucu dinamik istemci kaydını (RFC 7591)
 * desteklemiyorsa (Slack, Asana v2, Google) kullanıcı sağlayıcının
 * konsolunda bir uygulama açıp geri dönüş adresini
 * (`API_PUBLIC_URL` + `MCP_OAUTH_CALLBACK_PATH`) kaydediyor ve kimliği buraya
 * yazıyor. Sır yalnız gövdede gelir, şifreli saklanır, hiç geri dönmez.
 */
const oauthClientIdSchema = z.string().trim().min(1).max(512).regex(/^[\x21-\x7e]+$/, 'MCP_CLIENT_ID_INVALID');
const oauthClientSecretSchema = z.string().trim().min(1).max(2048).regex(/^[\x21-\x7e]+$/, 'MCP_CLIENT_SECRET_INVALID');

// ═══════════════════════════════════════════════════════════════════════════
// İstek / yanıt
// ═══════════════════════════════════════════════════════════════════════════

export const mcpConnectorCreateSchema = z
  .object({
    name: connectorNameSchema,
    url: mcpConnectorUrlSchema,
    authType: z.enum(MCP_CONNECTOR_AUTH_TYPES).default('NONE'),
    bearerToken: bearerTokenSchema.optional(),
    /** Yalnız OAUTH: önceden kayıtlı istemci (dinamik kayıt yoksa). */
    oauthClientId: oauthClientIdSchema.optional(),
    oauthClientSecret: oauthClientSecretSchema.optional(),
    /** Hazır listeden seçildiyse kimliği (yalnız denetim kaydı için). */
    presetId: z.string().max(40).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.authType === 'BEARER' && !value.bearerToken) {
      ctx.addIssue({ code: 'custom', path: ['bearerToken'], message: 'MCP_TOKEN_REQUIRED' });
    }
    if (value.authType !== 'BEARER' && value.bearerToken) {
      ctx.addIssue({ code: 'custom', path: ['bearerToken'], message: 'MCP_TOKEN_UNEXPECTED' });
    }
    if (value.authType !== 'OAUTH' && (value.oauthClientId || value.oauthClientSecret)) {
      ctx.addIssue({ code: 'custom', path: ['oauthClientId'], message: 'MCP_CLIENT_UNEXPECTED' });
    }
    if (value.oauthClientSecret && !value.oauthClientId) {
      ctx.addIssue({ code: 'custom', path: ['oauthClientId'], message: 'MCP_CLIENT_ID_REQUIRED' });
    }
  });
export type McpConnectorCreateInput = z.infer<typeof mcpConnectorCreateSchema>;

/**
 * Güncelleme. Adres ya da yetkilendirme türü değişirse kayıtlı jeton,
 * OAuth kaydı ve araç kataloğu SİLİNİR (başka bir sunucuya eski jetonu
 * göndermek bir sızıntı olurdu) — bağlantı yeniden test edilir.
 */
export const mcpConnectorUpdateSchema = z
  .object({
    name: connectorNameSchema.optional(),
    url: mcpConnectorUrlSchema.optional(),
    authType: z.enum(MCP_CONNECTOR_AUTH_TYPES).optional(),
    /** Yeni bearer jetonu (yalnız BEARER). Boş bırakılırsa kayıtlı jeton kalır. */
    bearerToken: bearerTokenSchema.optional(),
    /**
     * Önceden kayıtlı OAuth istemcisi (yalnız OAUTH). `null` = kayıtlı
     * istemciyi unut (dinamik kayda dön). Değişince kayıtlı jeton silinir.
     */
    oauthClientId: oauthClientIdSchema.nullable().optional(),
    oauthClientSecret: oauthClientSecretSchema.nullable().optional(),
    enabled: z.boolean().optional(),
    toolMode: z.enum(MCP_CONNECTOR_TOOL_MODES).optional(),
    allowedTools: z.array(mcpToolNameSchema).max(MCP_CONNECTOR_MAX_ALLOWED_TOOLS).optional(),
  })
  .strict();
export type McpConnectorUpdateInput = z.infer<typeof mcpConnectorUpdateSchema>;

/**
 * Satırın durumu — TÜRETİLMİŞ (kolon değil):
 *   · `DISABLED`   — kullanıcı kapattı
 *   · `NEEDS_AUTH` — OAuth seçili ama jeton yok / yenilenemedi, ya da bearer jetonu okunamıyor
 *   · `NEEDS_TEST` — hiç test edilmedi (araç listesi yok)
 *   · `PAUSED`     — istek anında ulaşılamadı; `pausedUntil`e kadar AI'a verilmiyor
 *   · `ERROR`      — son test başarısız
 *   · `READY`      — AI'ın isteklerine giriyor
 */
export const MCP_CONNECTOR_STATUSES = ['READY', 'NEEDS_TEST', 'NEEDS_AUTH', 'PAUSED', 'ERROR', 'DISABLED'] as const;
export type McpConnectorStatus = (typeof MCP_CONNECTOR_STATUSES)[number];

export interface McpConnectorRow {
  id: string;
  name: string;
  /** İstekte `mcp_servers[].name` — modelin gördüğü sunucu adı; değişmez. */
  slug: string;
  url: string;
  /** `url`in ana adı — satırda kalın görünen kısım. */
  host: string;
  authType: McpConnectorAuthType;
  /** Kayıtlı jeton var mı (değeri ASLA dönmez). */
  hasCredential: boolean;
  /**
   * OAuth istemcisi: elle girilen kimlik (`manual` — sır var mı ayrıca),
   * dinamik kayıtla alınan (`dynamic`) ya da henüz yok (`null`). Kimliğin
   * KENDİSİ gösterilebilir (sır değil); sır asla.
   */
  oauthClient: { mode: 'manual' | 'dynamic'; clientId: string; hasSecret: boolean } | null;
  /** OAuth erişim jetonunun bitişi (yenilenebilir); bilinmiyorsa `null`. */
  tokenExpiresAt: string | null;
  enabled: boolean;
  toolMode: McpConnectorToolMode;
  allowedTools: string[];
  /** Son başarılı `tools/list` — hiç okunmadıysa `null`. */
  tools: McpToolInfo[] | null;
  /** AI'ın kullanabildiği araçlardan okuma dışı olanların sayısı (uyarı rozeti). */
  writeToolsEnabled: number;
  status: McpConnectorStatus;
  lastCheckedAt: string | null;
  lastOkAt: string | null;
  lastError: McpConnectorErrorCode | null;
  lastErrorAt: string | null;
  pausedUntil: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface McpConnectorTestResult {
  ok: boolean;
  error: McpConnectorErrorCode | null;
  /** Sunucunun HTTP durumu (hata teşhisi için; gövde dönmez). */
  httpStatus: number | null;
  /** Sunucunun `serverInfo`su (ad, sürüm) — yalnız ekranda bilgi. */
  serverName: string | null;
  serverVersion: string | null;
  tools: McpToolInfo[] | null;
  /**
   * Sunucu 401 döndü ve OAuth meta verisi yayınlıyor — ekran "OAuth ile
   * bağlan"ı öneriyor.
   */
  oauthAvailable: boolean;
}

export interface McpConnectorMutationResult {
  connector: McpConnectorRow;
  test: McpConnectorTestResult | null;
}

export interface McpOAuthStartResponse {
  authUrl: string;
  expiresAt: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Hazır sunucular — ekranın "önerilenler" listesi
// ═══════════════════════════════════════════════════════════════════════════

export const MCP_PRESET_CATEGORIES = ['crm', 'commerce', 'marketing', 'support', 'productivity', 'payments'] as const;
export type McpPresetCategory = (typeof MCP_PRESET_CATEGORIES)[number];

export interface McpConnectorPreset {
  id: string;
  name: string;
  category: McpPresetCategory;
  /**
   * Sağlayıcının KENDİ belgesinde yayınladığı uzak MCP adresi. Doğrulanamayan
   * ya da kişiye özel olan (Zapier, mağaza başına Shopify) `null` — ekran
   * adresi kullanıcıya soruyor. 🔴 Adres UYDURULMAZ.
   */
  url: string | null;
  auth: McpConnectorAuthType;
  /** Adresin doğrulandığı resmî belge — ekranda "nereden bulurum" bağlantısı. */
  docsUrl: string | null;
}

/**
 * Doğrulama: 1 Eki 2026, sağlayıcıların kendi belgeleri (`docsUrl`). Yalnız
 * sağlayıcı sayfasında yayınlanan SABİT adresler dolu; kişiye/mağazaya özel
 * adres (Salesforce, Shopify, Zendesk, Zoho, Zapier) `null` — kullanıcı kendi
 * adresini yapıştırıyor. Bazı sağlayıcılar (Slack, Asana, Google) dinamik
 * istemci kaydı yerine önceden kayıtlı uygulama istiyor: ekran o durumda
 * istemci kimliği alanını ve geri dönüş adresini gösteriyor. Liste sırası
 * ekran sırası.
 */
export const MCP_CONNECTOR_PRESETS: readonly McpConnectorPreset[] = [
  { id: 'hubspot', name: 'HubSpot', category: 'crm', url: 'https://mcp.hubspot.com', auth: 'OAUTH', docsUrl: 'https://developers.hubspot.com/docs/build-with-ai/remote-mcp-server' },
  { id: 'salesforce', name: 'Salesforce', category: 'crm', url: null, auth: 'OAUTH', docsUrl: 'https://developer.salesforce.com/docs/platform/hosted-mcp-servers/guide/headless-360-mcp.html' },
  { id: 'pipedrive', name: 'Pipedrive', category: 'crm', url: 'https://mcp.pipedrive.ai/mcp', auth: 'OAUTH', docsUrl: 'https://support.pipedrive.com/en/article/mcp-claude' },
  { id: 'zoho-crm', name: 'Zoho CRM', category: 'crm', url: null, auth: 'OAUTH', docsUrl: 'https://www.zoho.com/crm/developer/mcp.html' },
  { id: 'shopify', name: 'Shopify', category: 'commerce', url: null, auth: 'NONE', docsUrl: 'https://shopify.dev/docs/apps/build/storefront-mcp' },
  { id: 'stripe', name: 'Stripe', category: 'payments', url: 'https://mcp.stripe.com', auth: 'OAUTH', docsUrl: 'https://docs.stripe.com/mcp' },
  { id: 'klaviyo', name: 'Klaviyo', category: 'marketing', url: 'https://mcp.klaviyo.com/mcp', auth: 'OAUTH', docsUrl: 'https://developers.klaviyo.com/en/docs/klaviyo_mcp_server' },
  { id: 'webflow', name: 'Webflow', category: 'marketing', url: 'https://mcp.webflow.com/mcp', auth: 'OAUTH', docsUrl: 'https://developers.webflow.com/mcp/reference/how-it-works' },
  { id: 'intercom', name: 'Intercom', category: 'support', url: 'https://mcp.intercom.com/mcp', auth: 'OAUTH', docsUrl: 'https://developers.intercom.com/docs/guides/mcp' },
  { id: 'zendesk', name: 'Zendesk', category: 'support', url: null, auth: 'OAUTH', docsUrl: 'https://support.zendesk.com/hc/en-us/articles/10497779528730' },
  { id: 'notion', name: 'Notion', category: 'productivity', url: 'https://mcp.notion.com/mcp', auth: 'OAUTH', docsUrl: 'https://developers.notion.com/guides/mcp/get-started-with-mcp' },
  { id: 'google-drive', name: 'Google Drive', category: 'productivity', url: 'https://drivemcp.googleapis.com/mcp/v1', auth: 'OAUTH', docsUrl: 'https://developers.google.com/workspace/drive/api/guides/configure-mcp-server' },
  { id: 'airtable', name: 'Airtable', category: 'productivity', url: 'https://mcp.airtable.com/mcp', auth: 'OAUTH', docsUrl: 'https://support.airtable.com/docs/using-the-airtable-mcp-server' },
  { id: 'slack', name: 'Slack', category: 'productivity', url: 'https://mcp.slack.com/mcp', auth: 'OAUTH', docsUrl: 'https://docs.slack.dev/ai/slack-mcp-server/' },
  { id: 'asana', name: 'Asana', category: 'productivity', url: 'https://mcp.asana.com/v2/mcp', auth: 'OAUTH', docsUrl: 'https://developers.asana.com/docs/connecting-mcp-clients-to-asanas-v2-server' },
  { id: 'monday', name: 'monday.com', category: 'productivity', url: 'https://mcp.monday.com/mcp', auth: 'OAUTH', docsUrl: 'https://developer.monday.com/api-reference/docs/mondaycom-mcp' },
  { id: 'atlassian', name: 'Jira & Confluence', category: 'productivity', url: 'https://mcp.atlassian.com/v2/mcp', auth: 'OAUTH', docsUrl: 'https://developer.atlassian.com/cloud/rovo-mcp/guides/getting-started/' },
  { id: 'linear', name: 'Linear', category: 'productivity', url: 'https://mcp.linear.app/mcp', auth: 'OAUTH', docsUrl: 'https://linear.app/docs/mcp' },
  { id: 'zapier', name: 'Zapier', category: 'productivity', url: null, auth: 'BEARER', docsUrl: 'https://docs.zapier.com/mcp/get-started/connect' },
];

// ═══════════════════════════════════════════════════════════════════════════
// Yardımcılar
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Görünen addan sunucu adı (`mcp_servers[].name`). Model bu adı görüyor ve
 * araç çağrısında (`server_name`) geri yazıyor: ASCII, küçük harf, kısa.
 * Çakışmayı ÇAĞIRAN çözüyor (sona `-2`, `-3`…).
 */
export function mcpConnectorSlugBase(name: string): string {
  const ascii = name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ı/g, 'i')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32)
    .replace(/-+$/g, '');
  return ascii.length >= 2 ? ascii : 'mcp';
}

/** `GET /workspaces/current/mcp-connectors` yanıtı. */
export interface McpConnectorListResponse {
  connectors: McpConnectorRow[];
  /**
   * OAuth geri dönüş adresi — önceden kayıtlı istemci açan kullanıcı bunu
   * sağlayıcının konsoluna yazıyor.
   */
  oauthRedirectUri: string;
  max: number;
}

/**
 * Uç hata kodları (`{ code, reason? }` gövdesi). `MCP_OAUTH_FAILED`
 * `reason` alanında bir `McpConnectorErrorCode` taşıyor.
 */
export const McpConnectorApiError = {
  NOT_FOUND: 'MCP_CONNECTOR_NOT_FOUND',
  LIMIT: 'MCP_CONNECTOR_LIMIT',
  NAME_TAKEN: 'MCP_CONNECTOR_NAME_TAKEN',
  /// OAuth başlatılamadı (keşif/kayıt) — `reason` ayrıntı kodu.
  OAUTH_FAILED: 'MCP_OAUTH_FAILED',
  /// Bağlantının yetkilendirme türü OAuth değil.
  NOT_OAUTH: 'MCP_CONNECTOR_NOT_OAUTH',
} as const;
export type McpConnectorApiError = (typeof McpConnectorApiError)[keyof typeof McpConnectorApiError];

export const mcpOAuthStartSchema = z
  .object({
    /** OAuth bitince dönülecek panel yolu — yalnız `/dashboard…` göreli yol. */
    returnTo: z
      .string()
      .max(200)
      .regex(/^\/dashboard(\/[\w\-/[\]]*)?$/, 'Yalnızca panel içi göreli yol')
      .default(MCP_CONNECTORS_PANEL_PATH),
  })
  .strict();
export type McpOAuthStartInput = z.infer<typeof mcpOAuthStartSchema>;
